<template>
	<div>
		<div class="d-flex align-items-center gap-2 mb-2">
			<span class="fw-semibold">Signal S-{{ signal.id }}</span>
			<span
				class="badge"
				:class="signal.addressed ? 'text-bg-secondary' : 'text-bg-warning'"
			>
				{{ signal.addressed ? "reviewed" : "unreviewed" }}
			</span>
			<button
				class="btn btn-sm btn-outline-secondary ms-auto"
				@click="emit('clear')"
			>
				<i class="bi bi-x-lg"></i>
			</button>
		</div>
		<div class="small text-muted mb-2">
			{{ signal.type }}
			<span v-if="signal.species"> · {{ signal.species }}</span>
			· {{ formatDate(signal.created) }}
			<span v-if="signal.site"> · {{ signal.site.label }}</span>
		</div>

		<div v-if="signal.addressed" class="d-flex gap-2 mb-2">
			<span class="badge text-bg-dark">{{ signal.provenance_class }}</span>
			<span class="badge" :class="effectBadge(signal.effect)">{{
				signal.effect
			}}</span>
			<span v-if="signal.weight" class="text-muted small"
				>weight {{ signal.weight }}</span
			>
		</div>

		<div v-else class="row g-2 mb-2">
			<div class="col-6">
				<label class="form-label small mb-1">Provenance class</label>
				<select
					v-model="form.provenance_class"
					class="form-select form-select-sm"
				>
					<option value="" disabled>Class…</option>
					<option
						v-for="value in provenanceClasses"
						:key="value"
						:value="value"
					>
						{{ value }}
					</option>
				</select>
			</div>
			<div class="col-6">
				<label class="form-label small mb-1">Effect</label>
				<select v-model="form.effect" class="form-select form-select-sm">
					<option value="" disabled>Effect…</option>
					<option v-for="value in effects" :key="value" :value="value">
						{{ value }}
					</option>
				</select>
			</div>
			<div class="col-12">
				<label class="form-label small mb-1">Weight (optional)</label>
				<input
					v-model.number="form.weight"
					type="number"
					min="0.01"
					step="0.01"
					class="form-control form-control-sm"
				/>
			</div>
			<div class="col-12">
				<button
					class="btn btn-sm btn-primary"
					:disabled="saving || !form.provenance_class || !form.effect"
					@click="review"
				>
					Review Signal
				</button>
			</div>
		</div>

		<button class="btn btn-sm btn-success w-100 mt-2" @click="emit('forge')">
			<i class="bi bi-plus-lg me-1"></i>Forge Lead from this Signal
		</button>
	</div>
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";

import type { PlanningSignal } from "@/type/api";

const props = defineProps<{ signal: PlanningSignal; saving: boolean }>();
const emit = defineEmits<{
	(
		e: "review",
		body: { provenance_class: string; effect: string; weight?: number },
	): void;
	(e: "forge"): void;
	(e: "clear"): void;
}>();

const provenanceClasses = [
	"production",
	"nuisance",
	"environmental",
	"administrative",
];
const effects = ["promotive", "suppressive", "contextual"];
const form = reactive({
	provenance_class: "",
	effect: "",
	weight: undefined as number | undefined,
});

watch(
	() => props.signal.id,
	() => {
		form.provenance_class = props.signal.provenance_class ?? "";
		form.effect = props.signal.effect ?? "";
		form.weight = props.signal.weight ?? undefined;
	},
	{ immediate: true },
);

function review() {
	emit("review", {
		provenance_class: form.provenance_class,
		effect: form.effect,
		...(form.weight && form.weight > 0 ? { weight: form.weight } : {}),
	});
}

function effectBadge(effect?: string | null) {
	switch (effect) {
		case "promotive":
			return "text-bg-success";
		case "suppressive":
			return "text-bg-danger";
		default:
			return "text-bg-secondary";
	}
}

function formatDate(value: string) {
	return new Date(value).toLocaleDateString();
}
</script>
